// info: all the information that the app needs to work in different components like the notifications 

import { createContext, useState, useEffect, useRef, useContext } from "react";
import { HubConnectionBuilder } from "@microsoft/signalr";
import SignalREncorehiveContext from "./signalREncorehive";

import { useDispatch } from "react-redux";
import { selectAuthStatus, selectCurrentUser, selectCurrentUserId } from "../features/auth/authSlice";
import { useLazyGetConfigsQuery } from "../services/configApi";
import { addBuyRequestsSafe, addSwarmSafe, addEventSafe, updateBuyRequest, updateSwarmSoftClosing, updateSwarm, updateBuyRequests, updateEvents } from "../store/slices/swarms";
import { useLazyGetSwarmsQuery, useLazyGetUserBuyRequestsQuery } from "../services/swarmsApi";
import { useLazyGetEventsQuery } from "../services/eventApi";
import { useLazyJobsQuery } from "../services/jobsApi";
import { useSelector } from "react-redux";

function eventsToDict(events) {
  let r = {};
  events.forEach(event => {
    if (event.success) {
      let eventId = event.eventModel.bteId;
      r[eventId] = event.eventModel;
    }
  })
  return r;
}

function getMessageFromConfig(configuration) {
  let systemMessage = configuration?.find(c => c.name == "systemMessage")?.value
  systemMessage = systemMessage && JSON.parse(systemMessage)
  return systemMessage
}


const AppContext = createContext();

export default AppContext;

export const AppProvider = ({ children }) => {
  const user = useSelector(selectCurrentUser);
  const userId = useSelector(selectCurrentUserId);
  const authStatus= useSelector(selectAuthStatus); //values: checking - authenticated - unauthenticated - failed

  const { signalREncorehive } = useContext(SignalREncorehiveContext);
  const [signalRUserConnection, setSignalRUserConnection] = useState(null)
  const [notificationsCount, setNotificationsCount] = useState(0);
  const [config, setConfig] = useState([])

  const [currentJob, setCurrentJob] = useState(null);
  const [fetchingCurrentJob, setFetchingCurrentJob] = useState(true);
  const [message, setMessage] = useState(null)

  const [ getAppConfigTrigger, { data: appConfiguration, isLoading: loadingConf, isFetching: fetchingAppConfiguration, error: getConfigError, isSuccess: configFetchSuccess }]= useLazyGetConfigsQuery();
  const [getBuyRequests] = useLazyGetUserBuyRequestsQuery()
  const [getSwarms] = useLazyGetSwarmsQuery()
  const [getEventsQuery]= useLazyGetEventsQuery()
  const [getJobsList] = useLazyJobsQuery()

  let dispatch = useDispatch();

  // * A: use Ref for event listeners
  let userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user])

  useEffect(async () => {
    if(authStatus !== 'authenticated') return; 

    try {
      const config = await getAppConfigTrigger(null, false).unwrap();
      console.log("App configuration fetched successfully: ", config)
      let parsedBooleans = config.map(conf => {
        if (conf.value == "true" || conf.value == "false") {
          return { ...conf, value: conf.value === "true" }
        } else {
          return conf
        }
      })
      setConfig(parsedBooleans);
    } catch (error) {
      if (error.status == 401) return; 
      console.error("AppProvider, error getting appConfiguration: " + JSON.stringify(error))
      throw error
    }

  }, [authStatus])

  if (getConfigError) {
    let message= "AppProvider, getConfigError: " + JSON.stringify(getConfigError);
    console.error(message);
    throw new Error(message);
  }

  useEffect(() => {
    if (config) {
      const newMessage = getMessageFromConfig(config);
      setMessage((prevMessage) => {
        if (JSON.stringify(prevMessage) !== JSON.stringify(newMessage)) {
          return newMessage;
        }
        return prevMessage; // Prevent state update if message hasn't changed
      });
    }
  }, [config]);
  

  useEffect(() => {
    if (!user || signalRUserConnection) {
      return; // Prevent new connection if user is not set or connection already exists
    }
  
    const userHubURL = (window.ENV?.VITE_ENCOREHIVE_REALTIME_SERVER_URL || import.meta.env.VITE_ENCOREHIVE_REALTIME_SERVER_URL) + "/usershub";
    console.log("userHubURL: ", userHubURL);
  
    const userConnection = new HubConnectionBuilder()
      .withUrl(userHubURL)
      .withAutomaticReconnect()
      .build();
  
    async function start() {
      try {
        await userConnection.start();
        setSignalRUserConnection(userConnection);
      } catch (err) {
        console.log(err);
        setTimeout(start, 5000); // Retry connection after 5 seconds
      }
    }
  
    userConnection.onclose(async () => {
      await start();
    });
  
    start();
  
    return () => {
      // Cleanup function to close the connection when the component unmounts
      if (userConnection) {
        userConnection.stop();
      }
    };
  }, [user, signalRUserConnection]);

  // END INIT SIGNAL R CONNECTION


  useEffect(() => { //A: get user current job
    async function getCurrentJob() {
      if(authStatus !== 'authenticated') return;

      let filter = {
        "EncoreHiveUserId": userId,
        "jobStatus": "In Progress"
      }

      let filterClean = "?";

      for (var [key, value] of Object.entries(filter)) {
        filterClean += `${key}=${value}&`;
      }

      let res = await getJobsList(filterClean, false).unwrap()
      if (res.status === "Ok") {
        let jobs = res.res.jobs;
        let events = res.res.events;

        let eventsDict = eventsToDict(events);

        jobs = jobs.reduce((pV, oneJob, cI) => {
          let eventInfo = eventsDict[oneJob.eventId];
          if (eventInfo)
            pV.push({ ...oneJob, ...eventsDict[oneJob.eventId] })
          return pV
        }, [])

        jobs = jobs.filter(job => job != null);
        let job = jobs[0];

        if (job) {
          let fixJobDate = {
            ...job,
            currentOwnerStarted: job?.currentOwnerStarted + 'Z'
          }
          setFetchingCurrentJob(false);
          setCurrentJob(fixJobDate);
        } else {
          setCurrentJob(job);
          setFetchingCurrentJob(false);
        }

      } else {
        console.log("getting job for user error: " + JSON.stringify(res))
      }
    }
    getCurrentJob();
  }, [userId, authStatus])

  useEffect(() => {
    if (signalREncorehive) {
      signalREncorehive.on("ConfigurationUpdated", function (newConfig) {
        console.log("signalR , appContext ConfigurationUpdated", newConfig)
        let parsedBooleans = newConfig.map(conf => {
          if (conf.value == "true" || conf.value == "false") {
            return { ...conf, value: conf.value === "true" }
          } else {
            return conf
          }
        })
        setConfig(parsedBooleans)
      });

      signalREncorehive.on("HardEscalate", function (jobId) {
        console.log("signalR , appContext job hard escalate jobId: " + jobId);
        if (!window.location.pathname.includes("dashboard/job")) { //A: if user is in dashboard/job we show him a modal, if we use setCurrentJob the modal dissapear
          setCurrentJob(null);
        }
      });

      signalREncorehive.on("CreateSwarmBuyRequest", function (newBuyRequest) {
        console.log("signalR , CreateSwarmBuyRequest " + JSON.stringify(newBuyRequest));
        dispatch(addBuyRequestsSafe(newBuyRequest));

        // get swarm and event data related to this buy request
        try {
          newBuyRequest.swarmId && getSwarms(`?Id=${newBuyRequest.swarmId}`).unwrap()
        } catch (error) {
          if (error.status == 401) return; //error managed on baseQueryWithReauth
          if (error.status == 404){
            console.error("Swarm not found, swarmId: " + newBuyRequest.swarmId," ", JSON.stringify(error))
            throw error
          }
        throw error
        }
      });

      signalREncorehive.on("CreateSwarmEvent", function (newSwarm) {
        console.log("signalR , CreateSwarmEvent " + JSON.stringify(newSwarm));
          dispatch(addSwarmSafe(newSwarm.newSwarm));//A: If this is the client that created the swarm, RTK Query took care of adding this new swarm to the Redux store. The function addNewSwarmSafe checks if it exists and does not add it in case it already exists
          dispatch(addEventSafe(newSwarm.eventData));
      });

      signalREncorehive.on("UpdateEvent", async function (editedSwarm) {
        console.log("signalR , UpdateEvent " + JSON.stringify(editedSwarm));
        dispatch(updateEvents([editedSwarm]));
      });

      signalREncorehive.on("EditSwarmEvent", async function (editedSwarm) {
        console.log("signalR , EditSwarmEvent " + JSON.stringify(editedSwarm));
        console.log("signalR , EditSwarmEvent, is on focus: ", window.location.search?.includes(editedSwarm.bteId.toString()));
        if(window.location.search?.includes(editedSwarm.bteId.toString())){
          console.log("signalR , EditSwarmEvent, is on focus");
          dispatch(updateSwarmSoftClosing(editedSwarm)); // adds hasBeenClosed property instead of running = false
        }else{
          console.log("signalR , EditSwarmEvent, is not on focus");
          dispatch(updateSwarm(editedSwarm))
        }
          // get event for this incomming swarm
          try {
            
            let query = `?bteId=${editedSwarm.bteId}`
            let res = await getEventsQuery(query).unwrap()
            console.log("DEBUG: res: ", res)
            dispatch(addEventSafe(res[0].eventModel));
          } catch (error) {
            if (error.status == 401) return; //error managed on baseQueryWithReauth
            if (error.status == 404){
              console.error("Event not found, eventId: " + editedSwarm.eventData.bteId," ", JSON.stringify(error))
              throw error
            }
            throw error
          
        }
      });

      signalREncorehive.on("EditSwarmBuyRequest", function (editedBuyRequest) {
        console.log("signalR , EditSwarmBuyRequest " + JSON.stringify(editedBuyRequest));
        dispatch(updateBuyRequest(editedBuyRequest));//A: If this is the client that created the swarm, RTK Query took care of adding this new swarm to the Redux store. The function addeditedSwarmSafe checks if it exists and does not add it in case it already exists
      });
      
      signalREncorehive.on("UpdateBuyRequestForUserAndEvent", function ({ userId, EventId }) {
        console.log("signalR , UpdateBuyRequestForUserAndEvent " + JSON.stringify({ userId, EventId }));
        if (userRef.current.role == "Admin" || userRef.current.useSwarm && userId === userRef.current.id) {
          getBuyRequests({ userId: userId })
        }
      });

    }
  }, [signalREncorehive, userRef.current]);

  let contextData = {
    signalRUserConnection,
    notificationsCount,
    setNotificationsCount,
    currentJob,
    setCurrentJob,
    fetchingCurrentJob,
    config,
    setConfig,
    message,
    setMessage
  };

  return (
    <AppContext.Provider value={contextData}>{children}</AppContext.Provider>
  );
};
 